import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxCell, CheckboxHeader, TextCell, TextWithHoverAction, LastOnlineCell } from '@/components/tables/contentFields';
import { ActionButtonCell } from '@/components/tables/contentFields';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import type { SortState } from '@/components/tables/SortDirection';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import TableLoadingView from '@/components/tables/TableLoadingView';
import { formatDate } from '@/utils/dateUtils';
import type { UserListItem } from '../types';

interface UsersTableProps {
  users: UserListItem[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  sortState?: SortState;
  onSortChange?: (column: string) => void;
  onUserClick: (user: UserListItem) => void;
  onCompanyClick: (companyId: string, companyName: string) => void;
}

export function UsersTable({
  users,
  isLoading,
  selectedIds,
  onSelectionChange,
  onUserClick,
  onCompanyClick,
}: UsersTableProps) {
  const { t } = useTranslation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const allSelected = users.length > 0 && selectedIds.size === users.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(users.map((u) => u.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  if (isLoading && users.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={5} withHeader={true} />
    );
  }

  if (users.length === 0) {
    return <EmptyTableView />;
  }

  return (
    <Table className="min-h-0 h-full" tableClassName="min-w-full divide-border">
      <TableHeader>
            <CheckboxHeader
              checked={allSelected}
              onChange={handleSelectAll}
              ariaLabel={t('users.table.selectAll')}
              indeterminate={someSelected}
            />

            <NonSortableColumnHeader
              label={t('users.table.columns.username')}
              minWidth="200px"
            />

            <NonSortableColumnHeader
              label={t('users.table.columns.email')}
              minWidth="200px"
            />

            <NonSortableColumnHeader
              label={t('users.table.columns.company')}
              minWidth="150px"
            />

            <NonSortableColumnHeader
              label={t('users.table.columns.userSince')}
              minWidth="120px"
            />

            <NonSortableColumnHeader
              label={t('users.table.columns.lastOnline')}
              minWidth="120px"
            />
      </TableHeader>

      <TableBody>
          {users.map((user) => (
            <tr
              key={user.id}
              className="hover:bg-surface-darker/50"
              onMouseEnter={() => setHoveredRowId(user.id)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <CheckboxCell
                checked={selectedIds.has(user.id)}
                onChange={() => handleSelectRow(user.id)}
                ariaLabel={t('users.table.selectRow', { name: user.username })}
              />

              <TextWithHoverAction
                value={user.username}
                isHovered={hoveredRowId === user.id}
                onCellClick={() => onUserClick(user)}
                onActionClick={() => onUserClick(user)}
                actionLabel={t('users.table.open')}
              />

              <TextCell value={user.email} />

              {user.company ? (
                <ActionButtonCell
                  text={user.company.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompanyClick(user.company!.id, user.company!.name);
                  }}
                />
              ) : (
                <TextCell value="-" />
              )}

              <TextCell value={formatDate(user.createdAt)} />

              <LastOnlineCell lastOnlineAt={user.lastOnlineAt} />
            </tr>
          ))}
      </TableBody>
    </Table>
  );
}
